// KI-Mitschrift eines Gesprächs: die lesbaren Teile der strukturierten Auswertung (call-analysis/v2, AI_PIPELINE.md).
// Nicht genannte Werte (0, UNKNOWN, leer) entfallen.

export type NoteParts = {
  summary: string;
  statements: string[];
  nextStep: string;
  callback: string;
  objections: string[];
  property: [string, string][];
  openQuestions: string[];
};

const objectionLabels: Record<string, string> = {
  NO_SALE_PLANNED: 'Kein Verkauf geplant',
  NO_TIME: 'Keine Zeit / ungünstiger Moment',
  HAS_AGENT: 'Hat bereits einen Makler',
  SELLS_PRIVATELY: 'Verkauft privat',
  COMMISSION: 'Provision / Kosten',
  DISTRUST: 'Misstrauen / schlechte Erfahrungen',
  PRICE_EXPECTATION: 'Preisvorstellung / Wert schon bekannt',
  FAMILY_DECISION: 'Entscheidung mit Familie / Miteigentümern',
  RENTED_OUT: 'Objekt vermietet',
  ALREADY_SOLD: 'Bereits verkauft',
  WANTS_INFO_ONLY: 'Nur Unterlagen / Infos gewünscht',
  DATA_PRIVACY: 'Datenschutz / Herkunft der Nummer',
  OTHER: 'Sonstiges',
};

const propertyTypes: Record<string, string> = {
  HOUSE: 'Haus',
  APARTMENT: 'Wohnung',
  MULTI_FAMILY: 'Mehrfamilienhaus',
  LAND: 'Grundstück',
  COMMERCIAL: 'Gewerbe',
  OTHER: 'Sonstiges',
};

const timeframes: Record<string, string> = {
  IMMEDIATE: 'sofort',
  WITHIN_3_MONTHS: 'in 3 Monaten',
  WITHIN_6_MONTHS: 'in 6 Monaten',
  WITHIN_12_MONTHS: 'in 12 Monaten',
  LATER: 'später',
};

const yesNo: Record<string, string> = { YES: 'ja', NO: 'nein' };

type Obj = Record<string, unknown>;

const obj = (v: unknown): Obj => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {});
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(str).filter(Boolean) : []);
const de = (n: number) => n.toLocaleString('de-DE');

function callbackText(cb: Obj): string {
  if (cb.required !== true) return '';
  const at = str(cb.datetime);
  const when = at ? new Date(at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '';
  return [when && !when.startsWith('Invalid') ? when : at, str(cb.note)].filter(Boolean).join(' – ') || 'gewünscht';
}

export function noteParts(output: unknown, summary: string | null | undefined): NoteParts {
  const o = obj(output);
  const p = obj(o.property);
  const property: [string, string][] = [];
  const add = (label: string, value: string) => value && property.push([label, value]);
  add('Objektart', propertyTypes[str(p.type)] ?? '');
  if (num(p.living_area_sqm)) add('Wohnfläche', `${de(num(p.living_area_sqm))} m²`);
  if (num(p.plot_area_sqm)) add('Grundstück', `${de(num(p.plot_area_sqm))} m²`);
  if (num(p.rooms)) add('Zimmer', de(num(p.rooms)));
  if (num(p.year_built)) add('Baujahr', String(num(p.year_built)));
  if (num(p.price_expectation_eur)) add('Preisvorstellung', `${de(num(p.price_expectation_eur))} €`);
  add('Verkauf', timeframes[str(p.sale_timeframe)] ?? '');
  add('Vermietet', yesNo[str(p.rented)] ?? '');
  add('Anderer Makler', yesNo[str(p.other_agent)] ?? '');
  add('Verkaufsgrund', str(p.sale_reason));
  const categories = list(o.objection_categories).map((c) => objectionLabels[c] ?? c);
  return {
    summary: str(summary) || str(o.summary),
    statements: list(o.key_statements),
    nextStep: str(obj(o.next_action).description),
    callback: callbackText(obj(o.callback)),
    objections: categories.length > 0 ? categories : list(o.objections),
    property,
    openQuestions: list(obj(o.brief).open_questions),
  };
}
